import { loadBaseCSS } from '../shared/button-utils.js';
import { enumAttribute, primitiveEvent, primitiveStyles } from '../shared/primitive-utils.js';
import { deepActiveElement, overlayContains } from '../shared/focus-utils.js';

export interface StepperStep { id: string; label: string; description?: string; completed?: boolean; disabled?: boolean; optional?: boolean; error?: string; }
interface StepNodes { item: HTMLLIElement; button: HTMLButtonElement; label: HTMLSpanElement; status: HTMLSpanElement; description: HTMLSpanElement; panel: HTMLElement; }
let stepperId = 0;

/**
 * Ordered task progress with persistent slotted content and guarded navigation.
 * @element qds-stepper
 * @attr {string} label - Accessible progress navigation name; default Task progress.
 * @attr {string} active-step - Current immutable step ID. Unknown IDs fall back until their step arrives.
 * @attr {boolean} linear - User navigation requires preceding nonoptional enabled steps to be complete.
 * @attr {'horizontal'|'vertical'} orientation - Progress layout; default horizontal, wraps on small screens.
 * @property {Array<{id: string; label: string; description?: string; completed?: boolean; disabled?: boolean; optional?: boolean; error?: string}>} steps - Property-only task sequence; IDs are unique and labels are plain text.
 * @property {string} activeStep - Current effective step ID; author assignments do not invoke user validation.
 * @slot step-ID - Persistent content for the step with ID ID, for example slot="step-review".
 * @fires {{value: string; previousValue: string; source: 'user'|'api'}} before-step-change - Cancelable navigation request; validate current content here.
 * @fires {{value: string; previousValue: string; source: 'user'|'api'}} step-change - Requested navigation completed.
 * @csspart navigation - Named progress navigation.
 * @csspart list - Ordered step list.
 * @csspart step - Native step button; aria-current=step identifies the current task.
 * @csspart status - Visible Completed, Current, Error or Not started text.
 * @csspart panel - Named active content region, focusable through focusPanel().
 */
export class QdsStepper extends HTMLElement {
  static get observedAttributes(): string[] { return ['label', 'active-step', 'linear', 'orientation']; }
  private _steps: StepperStep[] = [];
  private _fallback = '';
  private _nodes = new Map<string, StepNodes>();
  private _id = `qds-stepper-${++stepperId}`;
  private _serial = 0;
  private _onClick = (event: Event): void => {
    const button = event.composedPath().find(node => node instanceof HTMLButtonElement && node.hasAttribute('data-step')) as HTMLButtonElement | undefined;
    if (button && this.shadowRoot!.contains(button)) this._navigate(button.dataset.step!, 'user');
  };
  private _onKeydown = (event: KeyboardEvent): void => {
    const current = this.shadowRoot!.activeElement;
    if (!(current instanceof HTMLButtonElement) || !current.hasAttribute('data-step') || event.altKey || event.ctrlKey || event.metaKey) return;
    const buttons = this._steps.map(step => this._nodes.get(step.id)!.button).filter(button => !button.disabled);
    const index = buttons.indexOf(current), vertical = this.orientation === 'vertical', rtl = getComputedStyle(this).direction === 'rtl';
    const next = vertical ? 'ArrowDown' : rtl ? 'ArrowLeft' : 'ArrowRight';
    const previous = vertical ? 'ArrowUp' : rtl ? 'ArrowRight' : 'ArrowLeft';
    let target: HTMLButtonElement | undefined;
    if (event.key === 'Home') target = buttons[0];
    else if (event.key === 'End') target = buttons[buttons.length-1];
    else if (event.key === next) target = buttons[(index + 1) % buttons.length];
    else if (event.key === previous) target = buttons[(index - 1 + buttons.length) % buttons.length];
    if (target) { event.preventDefault(); target.focus(); }
  };
  constructor() {
    super();
    this.attachShadow({mode:'open'}).innerHTML = `<style>${primitiveStyles}
      :host { display: block; }
      ol { display: flex; flex-wrap: wrap; gap: .75rem; margin: 0; padding: 0; list-style: none; }
      li { flex: 1 1 10rem; min-width: 0; }
      button { display: flex; flex-direction: column; align-items: flex-start; gap: .25rem; width: 100%; min-height: 44px;
        border: 1px solid var(--theme-border-primary, #e0e0e0); border-radius: 4px; padding: .75rem;
        text-align: start; overflow-wrap: anywhere; background: var(--theme-background-primary, white); color: var(--theme-text-primary, #333); cursor: pointer; }
      button[aria-current] { border-inline-start: 4px solid var(--theme-accent-primary, #445e89); background: var(--theme-background-secondary, #f9fafb); }
      button:hover:not(:disabled) { background: var(--theme-background-hover, #f0f0f0); }
      button:disabled { cursor: not-allowed; border-style: dashed; }
      [part=status], .description { font-size: .875em; }
      [part=panel] { padding-block: 1rem; outline-offset: 2px; }
      [part=panel]:focus-visible { outline: 2px solid var(--theme-accent-primary, #445e89); }
      :host([orientation=vertical]) ol { flex-direction: column; }
      :host([orientation=vertical]) li { flex-basis: auto; }
      @media (forced-colors: active) { button[aria-current] { border-color: Highlight; } button:disabled { color: GrayText; } }
    </style><nav part="navigation"><ol part="list"></ol></nav><div id="panels"></div>`;
    loadBaseCSS();
  }
  connectedCallback(): void {
    for (const name of ['steps','activeStep','label','linear','orientation'] as const) {
      if(Object.prototype.hasOwnProperty.call(this,name)){
        const value=this[name];Reflect.deleteProperty(this,name);Reflect.set(this,name,value);
      }
    }
    this.shadowRoot!.addEventListener('click',this._onClick);
    this.shadowRoot!.addEventListener('keydown',this._onKeydown as EventListener);
    this._sync();
  }
  disconnectedCallback(): void {
    this.shadowRoot!.removeEventListener('click',this._onClick);
    this.shadowRoot!.removeEventListener('keydown',this._onKeydown as EventListener);
  }
  attributeChangedCallback(name:string): void { if(name==='active-step'&&!this.getAttribute(name))this._fallback='';if(this.isConnected)this._sync(); }
  get steps(): StepperStep[] { return this._steps.map(step=>({...step})); }
  set steps(value: ReadonlyArray<StepperStep>) {
    const ids=new Set<string>();
    this._steps=Array.isArray(value)?value.flatMap(step=>{
      if(!step || typeof step.id!=='string' || !step.id.trim() || typeof step.label!=='string' || !step.label.trim() || ids.has(step.id.trim()))return [];
      const id=step.id.trim();ids.add(id);
      return [{id,label:step.label.trim(),description:typeof step.description==='string'?step.description.trim():'',completed:!!step.completed,disabled:!!step.disabled,optional:!!step.optional,error:typeof step.error==='string'?step.error.trim():''}];
    }):[];
    this._sync();
  }
  get label(): string { return this.getAttribute('label') || 'Task progress'; }
  set label(value:string) { this.setAttribute('label',value??''); }
  get linear(): boolean { return this.hasAttribute('linear'); }
  set linear(value:boolean) { this.toggleAttribute('linear',!!value); }
  get orientation(): 'horizontal'|'vertical' { return enumAttribute(this,'orientation',['horizontal','vertical'],'horizontal'); }
  set orientation(value:'horizontal'|'vertical') { this.setAttribute('orientation',value); }
  get activeStep(): string { const requested=this.getAttribute('active-step');return this._steps.find(step=>step.id===requested&&!step.disabled)?.id || this._steps.find(step=>step.id===this._fallback&&!step.disabled)?.id || this._steps.find(step=>!step.disabled)?.id || ''; }
  set activeStep(value:string) { this.setAttribute('active-step',typeof value==='string'?value:''); }
  /** Request navigation with the same completion/disabled/cancelation policy as a click. */
  goTo(value:string): boolean { return this._navigate(value,'api'); }
  /** Focus the current content region after application rendering; does not focus a field or submit. */
  focusPanel(options?:FocusOptions): void { this._nodes.get(this.activeStep)?.panel.focus(options); }
  private _available(index:number): boolean { return !this._steps[index].disabled && (!this.linear || index<=this._steps.findIndex(step=>step.id===this.activeStep) || this._steps.slice(0,index).every(step=>step.disabled||step.optional||(step.completed&&!step.error))); }
  private _navigate(value:string,source:'user'|'api'):boolean {
    const index=this._steps.findIndex(step=>step.id===value),previousValue=this.activeStep;
    if(index<0 || !this._available(index) || previousValue===value)return false;
    const detail={value,previousValue,source};
    if(!primitiveEvent(this,'before-step-change',{...detail},true))return false;
    // A validation listener may replace the sequence; never activate a removed/disabled step.
    const currentIndex=this._steps.findIndex(step=>step.id===value);
    if(currentIndex<0 || !this._available(currentIndex) || this.activeStep!==previousValue)return false;
    this.activeStep=value;
    primitiveEvent(this,'step-change',detail);
    return true;
  }
  private _sync():void {
    const root=this.shadowRoot!,list=root.querySelector('ol')!,panels=root.querySelector('#panels')!,active=deepActiveElement();
    const focused=active&&overlayContains(this,active)?active:null;
    const focusedStep=[...this._nodes].find(([,nodes])=>nodes.button===focused)?.[0];
    const oldActive=[...this._nodes].find(([,nodes])=>!nodes.panel.hidden)?.[0];
    root.querySelector('nav')!.setAttribute('aria-label',this.label);
    root.querySelector<HTMLElement>('nav')!.hidden=!this._steps.length;
    const effective=this.activeStep;
    this._fallback=effective;
    for(const [id,nodes]of this._nodes)if(!this._steps.some(step=>step.id===id)){nodes.item.remove();nodes.panel.remove();this._nodes.delete(id);}
    this._steps.forEach((step,index)=>{
      let nodes=this._nodes.get(step.id);
      if(!nodes){
        const item=document.createElement('li'),button=document.createElement('button'),label=document.createElement('span'),status=document.createElement('span'),description=document.createElement('span'),panel=document.createElement('section'),slot=document.createElement('slot');
        button.type='button';button.dataset.step=step.id;button.id=`${this._id}-step-${++this._serial}`;button.setAttribute('part','step');label.className='label';status.setAttribute('part','status');description.className='description';
        panel.id=`${button.id}-panel`;panel.setAttribute('part','panel');panel.setAttribute('role','region');panel.setAttribute('aria-labelledby',button.id);panel.tabIndex=-1;slot.name=`step-${step.id}`;
        button.setAttribute('aria-controls',panel.id);button.append(label,status,description);item.append(button);panel.append(slot);nodes={item,button,label,status,description,panel};this._nodes.set(step.id,nodes);
      }
      nodes.label.textContent=`${index+1}. ${step.label}`;
      nodes.status.textContent=[step.id===effective?'Current':step.error?'Error':step.completed?'Completed':'Not started',step.optional?'Optional':'',step.disabled?'Unavailable':'',step.error||''].filter(Boolean).join(' · ');
      nodes.description.textContent=step.description||'';nodes.description.hidden=!step.description;
      // Current remains operable for semantics; activating it is a no-op.
      nodes.button.disabled=step.disabled||(!this._available(index)&&step.id!==effective);
      if(step.id===effective)nodes.button.setAttribute('aria-current','step');else nodes.button.removeAttribute('aria-current');
      nodes.panel.hidden=step.id!==effective;
      if(list.children[index]!==nodes.item)list.insertBefore(nodes.item,list.children[index]||null);
      if(panels.children[index]!==nodes.panel)panels.insertBefore(nodes.panel,panels.children[index]||null);
    });
    if(focusedStep){const candidate=this._nodes.get(focusedStep)?.button;(candidate&&!candidate.disabled?candidate:this._nodes.get(effective)?.button)?.focus();}
    else if(focused && oldActive!==effective)this._nodes.get(effective)?.panel.focus();
    else if(focused?.isConnected)focused.focus({preventScroll:true});
  }
}
if(!customElements.get('qds-stepper'))customElements.define('qds-stepper',QdsStepper);
